import { useState } from "react";
import { ExternalLink, Image as ImageIcon } from "lucide-react";
import { Alert, Button, Modal } from "@/shared/rd";
import { Meta } from "../rd-local";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoreSubscriptionPaymentProofUrlQuery } from "@/shared/stores/services/queries/useStoreSubscriptionPaymentProofUrlQuery";

type Props = {
  storeId: string;
  paymentId: string;
};

export function SubscriptionPaymentProofModal({ storeId, paymentId }: Props) {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageAttempt, setImageAttempt] = useState(0);
  const {
    data,
    isLoading,
    isFetching,
    error: proofError,
    refetch,
  } = useStoreSubscriptionPaymentProofUrlQuery({
    storeId,
    paymentId,
    enabled: isOpen,
  });

  const openModal = () => {
    setImageFailed(false);
    setIsImageLoading(true);
    setIsOpen(true);
    void refetch();
  };
  const retryProof = () => {
    setImageFailed(false);
    setIsImageLoading(true);
    setImageAttempt((attempt) => attempt + 1);
    void refetch();
  };

  const proofUrl = data?.url ?? null;
  const isMissing = proofError instanceof ApiJsonClientError && proofError.status === 404;
  const error = proofError && !isMissing
    ? proofError instanceof ApiJsonClientError
      ? proofError.message || t(keys.errors.generic)
      : t(keys.errors.generic)
    : imageFailed ? t(keys.errors.generic) : null;

  // Restart the image spinner whenever a new proof URL arrives
  // (adjust-state-during-render pattern instead of an effect).
  const [prevProofUrl, setPrevProofUrl] = useState(proofUrl);
  if (proofUrl !== prevProofUrl) {
    setPrevProofUrl(proofUrl);
    setIsImageLoading(Boolean(proofUrl));
    setImageFailed(false);
  }

  return (
    <>
      <Button size="sm" icon={ImageIcon} onClick={openModal}>
        {t(keys.admin.subscriptionPaymentProofViewAction)}
      </Button>
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={t(keys.admin.subscriptionPaymentProofTitle)}
        width={640}
        actions={
          <Button variant="ghost" muted onClick={() => setIsOpen(false)}>
            {t(keys.common.close)}
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p className="rd-modal-text" style={{ margin: 0 }}>
            {t(keys.admin.subscriptionPaymentProofHint)}
          </p>
          {error ? (
            <div style={{ alignSelf: "stretch" }}>
              <Alert
                tone="error"
                action={
                  <Button size="sm" loading={isFetching} loadingLabel={t(keys.common.loading)} onClick={retryProof}>
                    {t(keys.common.retry)}
                  </Button>
                }
              >
                {error}
                <span style={{ display: "block", marginTop: 2 }}>{t(keys.errors.recovery)}</span>
              </Alert>
            </div>
          ) : isLoading ? (
            <Meta>{t(keys.common.loading)}</Meta>
          ) : proofUrl && !isMissing ? (
            <>
              <a
                href={proofUrl}
                target="_blank"
                rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, color: "var(--rd-primary)" }}
              >
                {t(keys.admin.subscriptionPaymentProofOpenTab)}
                <ExternalLink size={14} aria-hidden="true" />
              </a>
              <div
                style={{
                  position: "relative",
                  height: 360,
                  overflow: "hidden",
                  borderRadius: "var(--rd-radius-card)",
                  background: "var(--rd-surface-sunken)",
                  boxShadow: "var(--rd-shadow-card)",
                }}
              >
                {isImageLoading ? (
                  <span
                    aria-hidden="true"
                    style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 13, color: "var(--rd-text-tertiary)" }}
                  >
                    {t(keys.common.loading)}
                  </span>
                ) : null}
                <img
                  key={`${proofUrl}:${imageAttempt}`}
                  src={proofUrl}
                  alt={t(keys.admin.subscriptionPaymentProof)}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: isImageLoading ? 0 : 1,
                    transition: "opacity .3s",
                  }}
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => {
                    setIsImageLoading(false);
                    setImageFailed(true);
                  }}
                />
              </div>
            </>
          ) : (
            <Meta>{t(keys.admin.subscriptionPaymentProofMissing)}</Meta>
          )}
        </div>
      </Modal>
    </>
  );
}
